import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { MultiSelect } from 'primereact/multiselect';
import ButtonSp from '../../components/ButtonSp';
import DropDownLazy from '../../components/DropdownLazy';
import DropdownSp from '../../components/DropdownSp';

import InputTextSp from '../../components/InputTextSp';
import LabelSp from '../../components/LabelSp';
import { showMessage } from '../../components/MessageDialog';
import AuthService from '../../services/AuthService';

import UsuarioService from '../../services/UsuarioService';
import ColaboradorService from '../../services/ColaboradorService';

import { errorHandle, validateFields } from '../../util/functions';
import { UsuarioModel } from '../../util/Models';
import { StateScreen } from '../constants';
import UsuarioPerfilService from '../../services/UsuarioPerfilService';
import ChamadoTipoService from '../../services/ChamadoTipoService';

function UsuarioCrud(props) {
  UsuarioCrud.propTypes = {
    stateScreen: PropTypes.string.isRequired,
    idSelected: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  UsuarioCrud.defaultProps = {
    messageConfirmation: 'Confirma os dados',
    showConfirmation: false,
  };

  const { stateScreen, idSelected, onClose } = props;

  // useMemos
  const usuarioLogado = useMemo(() => AuthService.getUsuario(), []);

  // states
  const [usuario, setUsuario] = useState(new UsuarioModel());
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState(null);

  const [senha, setSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [errorLoadRecord, setErrorLoadRecord] = useState(false);
  const [perfils, setPerfils] = useState();
  const [listaChamadoTiposSelecionado, setListaChamadoTiposSelecionado] = useState([]);
  const [chamadoTipos, setChamadoTipos] = useState();

  // useCallbacks
  const loadColaborador = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await ColaboradorService.findAll({ nome: _nome, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const loadChamadoTipo = useCallback(async () => {
    const r = await ChamadoTipoService.findAll({
      limit: 999,
    });

    const retorno = r.items
      .sort((a, b) => {
        if (a.nome < b.nome) {
          return -1;
        }
        if (a.nome > b.nome) {
          return 1;
        }
        return 0;
      })
      .map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });

    setChamadoTipos(retorno);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPerfils = useCallback(async () => {
    try {
      const r = await UsuarioPerfilService.findAll({ limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      setPerfils(retorno);
    } catch (err) {
      setErrorLoadRecord(true);
      errorHandle(err);
    }
  }, []);

  const loadRecord = useCallback(async _id => {
    try {
      const retorno = await UsuarioService.findById(_id);

      setUsuario(retorno);
      const colaborador = retorno.colaborador
        ? {
            label: retorno.colaborador.nome,
            value: retorno.colaborador.id,
          }
        : null;

      setColaboradorSelecionado(colaborador);

      const selecionados = retorno.usuarioChamadoTipo
        .sort((a, b) => {
          if (a.chamadoTipo.nome < b.chamadoTipo.nome) {
            return -1;
          }
          if (a.chamadoTipo.nome > b.chamadoTipo.nome) {
            return 1;
          }

          return 0;
        })
        .filter(e => e.ativo === true)
        .map(e => e.idChamadoTipo);

      setListaChamadoTiposSelecionado(selecionados);

      setErrorLoadRecord(false);
    } catch (err) {
      setErrorLoadRecord(true);
      errorHandle(err);
    }
  }, []);

  // funcoes
  function viewMode() {
    return stateScreen === StateScreen.stView || errorLoadRecord;
  }

  function handleBack() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      showMessage('Confirmação', 'Abandonar mudanças?', idx => {
        if (idx === 1) {
          onClose();
        }
      });
    }
  }

  function handleSave() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      salvarRecord();
    }
  }

  async function salvarRecord() {
    if (senha || confirmSenha) {
      if (senha !== confirmSenha) {
        toast.warn('Senha e confirmação não conferem.');
        return;
      }

      usuario.senha = senha;
      usuario.confirmSenha = confirmSenha;
    }

    // ajustando tipo chamado
    chamadoTipos.forEach(c => {
      if (usuario.usuarioChamadoTipo.findIndex(u => u.idChamadoTipo === c.value) < 0)
        usuario.usuarioChamadoTipo.push({
          idUsuario: idSelected,
          idChamadoTipo: c.value,
          ativo: false,
        });
    });

    // marcando todos como inativos
    usuario.usuarioChamadoTipo.forEach(u => {
      u.ativo = false;
    });

    listaChamadoTiposSelecionado.forEach(c => {
      const idx = usuario.usuarioChamadoTipo.findIndex(u => u.idChamadoTipo === c);

      if (idx >= 0) {
        usuario.usuarioChamadoTipo[idx].ativo = true;
      }
    });

    try {
      let retorno;
      if (stateScreen === StateScreen.stInsert) {
        retorno = await UsuarioService.insert(usuario);
      } else {
        retorno = await UsuarioService.update(usuario);
      }
      toast.success('Registro salvo com sucesso.');
      onClose(retorno);
    } catch (err) {
      errorHandle(err);
    }
  }

  // useEffects
  useEffect(() => {
    const asyncFunc = async () => {
      await loadPerfils();
      await loadChamadoTipo();
      if (stateScreen === StateScreen.stUpdate || stateScreen === StateScreen.stView) {
        loadRecord(idSelected);
      } else if (stateScreen === StateScreen.stInsert) {
        const novo = new UsuarioModel();
        setColaboradorSelecionado(null);

        setUsuario(novo);
      }
      setSenha('');
      setConfirmSenha('');
    };
    asyncFunc();
  }, [loadRecord, idSelected, stateScreen, loadPerfils, loadChamadoTipo]);

  const perfilAdministrador = AuthService.isPerfilGerente();

  // render principal
  return (
    <>
      <>
        <div className="p-col-12 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Login</LabelSp>
          <InputTextSp
            autoFocus
            value={usuario.login}
            maxLength={100}
            required
            disabled={viewMode()}
            onChange={e => {
              setUsuario({ ...usuario, login: (e.target.value || '').toLowerCase() });
            }}
          />
        </div>

        <div className="p-col-12 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Perfil</LabelSp>
          <DropdownSp
            placeholder="Selecione"
            required
            disabled={viewMode() || !perfilAdministrador}
            value={usuario.idUsuarioPerfil}
            options={perfils}
            onChange={e => {
              setUsuario({ ...usuario, idUsuarioPerfil: e.value });
            }}
          />
        </div>

        <div className="p-col-12 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Colaborador</LabelSp>
          <DropDownLazy
            required
            autoLoad
            disabled={viewMode() || !perfilAdministrador}
            placeholder="Selecione"
            onChange={e => {
              const colaborador = e ? { id: e.value, nome: e.label } : null;
              setUsuario({
                ...usuario,
                colaborador,
                idColaborador: e?.value,
              });
            }}
            value={colaboradorSelecionado}
            onFilter={async txtFilter => {
              const retorno = await loadColaborador(txtFilter);
              return retorno;
            }}
          />
        </div>

        <div className="p-col-6 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Inativo?</LabelSp>
          <DropdownSp
            required
            value={usuario.inativo}
            options={[
              { label: 'Sim', value: true },
              { label: 'Não', value: false },
            ]}
            disabled={viewMode() || !perfilAdministrador}
            filterInputAutoFocus={false}
            onChange={e => {
              setUsuario({ ...usuario, inativo: e.target.value });
            }}
          />
        </div>

        <div className="p-col-12 p-md-8 p-lg-8 p-fluid">
          <LabelSp>Tipo Chamado</LabelSp>
          <MultiSelect
            value={listaChamadoTiposSelecionado}
            options={chamadoTipos}
            maxSelectedLabels={6}
            onChange={e => {
              setListaChamadoTiposSelecionado(e.value);
            }}
          />
        </div>

        <div className="p-col-6 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Bloq. Filtro Chamado?</LabelSp>
          <DropdownSp
            required
            value={usuario.bloqueiaFiltroTipoChamado}
            options={[
              { label: 'Sim', value: true },
              { label: 'Não', value: false },
            ]}
            disabled={viewMode() || !perfilAdministrador}
            filterInputAutoFocus={false}
            onChange={e => {
              setUsuario({ ...usuario, bloqueiaFiltroTipoChamado: e.target.value });
            }}
          />
        </div>

        <div className="p-col-6 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Senha</LabelSp>
          <InputTextSp
            type="password"
            name="txtSenha"
            maxLength={16}
            disabled={
              viewMode() ||
              (usuarioLogado.id !== 1 &&
                usuario.id !== usuarioLogado.id &&
                !perfilAdministrador)
            }
            required={stateScreen === StateScreen.stInsert || !!confirmSenha}
            value={senha}
            onChange={e => {
              setSenha(e.target.value);
            }}
          />
        </div>

        <div className="p-col-6 p-sm-4 p-lg-4 p-fluid">
          <LabelSp>Confirmação</LabelSp>
          <InputTextSp
            type="password"
            name="txtSenhaConfirmacao"
            maxLength={16}
            disabled={
              viewMode() ||
              (usuarioLogado.id !== 1 &&
                usuario.id !== usuarioLogado.id &&
                !perfilAdministrador)
            }
            required={stateScreen === StateScreen.stInsert || !!senha}
            value={confirmSenha}
            onChange={e => {
              setConfirmSenha(e.target.value);
            }}
          />
        </div>

        <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
          {!viewMode() ? (
            <ButtonSp
              className="p-button-success"
              icon="pi pi-save"
              label="Salvar"
              keyAccess="S"
              disabled={
                !validateFields(usuario, ['login', 'idColaborador']) ||
                (stateScreen === StateScreen.stInsert && (!senha || !confirmSenha))
              }
              showConfirmation
              onClick={handleSave}
            />
          ) : null}
          <ButtonSp
            className="p-button-secondary"
            label="Voltar"
            keyAccess="V"
            icon="pi pi-chevron-circle-left"
            onClick={handleBack}
          />
        </div>
      </>
    </>
  );
}

export default UsuarioCrud;
